import {useCallback, useState, useMemo} from 'react';
import {AnalyticsTodayDate} from '@utils/date';
import {useQuery} from 'react-query';
import {usePeriodHook} from '@hooks/period-hook';
import {useDirectOptionHook} from '@pages/dashboard/reporting/reporting-pages/reporting-overview/hooks/direct-options.hook';
import {CompanyGoalsRequestImpl} from './direct-report-request';
import {useStoreContext} from '@store/store-context';
import {CompanyGoalsController} from './direct-report-controller';
import {periodFromValue} from '../my-objective/my-goals-v2.utils';
import {
  goalMemberBucketsForView,
  goalMemberBucketsWithCarryover,
} from './direct-report-response';
export interface IUser {
  label: '';
  value: '';
}

export const useDirectReportsGoal = (scope: 'team' | 'all' = 'team') => {
  const {
    companyStore: {searchable, setDirectReports},
    authStore: {auth},
    groupStore: {groups},
    storeDashboardRoutes: {GoalViewTabs},
  } = useStoreContext();

  const {directReportsTeamOption} = useDirectOptionHook();

  const [userFilter, setUserFilter] = useState({
    filterBy: {
      label: 'Teams',
      value: 'team',
    },
    selectedTeam: directReportsTeamOption[0],
    members: [] as IUser[],
  });

  const [searchValue, setSearchValue] = useState('');

  const filters = useMemo(() => {
    if (scope === 'all') {
      return {
        member:
          userFilter.members.length > 0
            ? userFilter.members.map((member) => member.value).join(',')
            : undefined,
      };
    }

    if (searchable?.member && searchable?.manager) {
      return {
        member: searchable?.member,
        name: searchValue,
        manager: searchable?.manager,
      };
    } else if (!searchable?.member) {
      return {
        manager:
          !userFilter.selectedTeam.value ||
          userFilter.filterBy.value === 'people'
            ? userFilter.members.length
              ? undefined
              : auth.user.id
            : userFilter.selectedTeam.value,
        name: searchValue,

        member:
          userFilter.members.length > 0
            ? userFilter.members.map((member) => member.value).join(',')
            : undefined,
      };
    } else {
      return {
        member: searchable?.member,
        name: searchValue,

        manager: searchable?.manager,
      };
    }
  }, [
    auth.user.id,
    searchable?.member,
    searchable?.manager,
    userFilter,
    searchValue,
    scope,
  ]);

  const computeView = useCallback(() => {
    if (GoalViewTabs.flat) {
      return 'flat';
    } else if (GoalViewTabs.nested) {
      return 'stacked';
    } else {
      return 'tree';
    }
  }, [GoalViewTabs.flat, GoalViewTabs.nested]);

  const request = useMemo(() => {
    return new CompanyGoalsRequestImpl();
  }, []);

  const controller = useMemo(() => {
    return new CompanyGoalsController(request);
  }, [request]);

  const initDirectReportsGoals = useCallback(
    async (date: {starts: string; ends: string}) => {
      const requestFilters = {
        ...filters,
        name: searchValue,
        sortByUsers: scope !== 'all',
      };
      const [activeResponse, workspaceResponse] = await Promise.all([
        controller.fetchTeamMemberGoals(requestFilters),
        controller.fetchWorkspaceGoals(requestFilters),
      ]);

      if (!activeResponse || !workspaceResponse) {
        setDirectReports([]);
        return false;
      }

      const response = goalMemberBucketsForView(
        {
          users: goalMemberBucketsWithCarryover(
            activeResponse,
            workspaceResponse,
            periodFromValue(date),
          ),
        },
        computeView(),
      );

      if (response) {
        const arrayOfAssigneesGoals = response.map((res: any) => {
          return {
            assignee: res,
            goals:
              res.goals &&
              res.goals.map((goal: any) => {
                if (goal.goalType === 'group') {
                  const group = groups.filter((group) =>
                    goal.group.includes(group.id),
                  );

                  return {
                    ...goal,
                    assignee: goal.assignee,
                    group,
                  };
                } else
                  return {
                    ...goal,
                    assignee: goal.assignee,
                  };
              }),
          };
        });
        response && setDirectReports(arrayOfAssigneesGoals);
        return arrayOfAssigneesGoals;
      }

      setDirectReports([]);
      return [];
    },
    [
      controller,
      filters,
      searchValue,
      computeView,
      setDirectReports,
      groups,
      scope,
    ],
  );

  return {
    initDirectReportsGoals,
    filters,
    view: computeView(),
    setUserFilter,
    userFilter,
    setSearchValue,
  };
};

export const useDirectReportHook = (scope: 'team' | 'all' = 'team') => {
  const [clearSelection, setClearSelection] = useState(false);

  const {
    initDirectReportsGoals,
    filters,
    setUserFilter,
    userFilter,
    setSearchValue,
    view,
  } = useDirectReportsGoal(scope);

  const {currentMonthRange, isPeriodEnabled} = usePeriodHook();

  const getPeriod = useMemo(() => {
    const range = isPeriodEnabled
      ? currentMonthRange()
      : {
          starts: AnalyticsTodayDate().AnalyticsDefaultStartDate,
          ends: AnalyticsTodayDate().AnalyticsDefaultEndDate,
        };
    return periodFromValue(range);
  }, [isPeriodEnabled, currentMonthRange]);

  const [dateRange, setDateRange] = useState(getPeriod);

  const {data, isError, isLoading, refetch} = useQuery(
    [
      scope === 'all' ? 'all_goals' : 'direct_reports',
      {...filters, dateRange, view},
    ],
    () => initDirectReportsGoals(dateRange),
  );

  return {
    loading: isLoading,
    goalMembers: Array.isArray(data) ? data : [],
    userFilter,
    refetch,
    setUserFilter,
    clearSelection,
    setSearchValue,
    setClearSelection,
    dateRange,
    setDateRange,
    filters,
    view,
    error: isError || data === false,
  };
};
